import React, { useEffect, useRef, useState } from 'react';
import { DayPicker, DateRange as RDPRange } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { useTheme } from '../../../../theme-context';

type DateRange = [Date, Date] | null;

interface Props {
    label: string;
    labelStyle?: string;
    containerStyle?: string;
    value: DateRange;
    onChange: (range: DateRange) => void;
}

const formatDate = (date: Date): string =>
    `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

const DateRangePopover = ({
    label,
    labelStyle,
    containerStyle,
    value,
    onChange,
}: Props) => {
    const { theme } = useTheme();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const [range, setRange] = useState<RDPRange | undefined>(() =>
        value ? { from: value[0], to: value[1] } : undefined,
    );

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (range?.from && range?.to) {
            onChange([range.from, range.to]);
        }
    }, [range]);

    const applyShortcut = (
        type:
            | 'today'
            | 'yesterday'
            | 'thisWeek'
            | 'lastWeek'
            | 'thisMonth'
            | 'lastMonth',
    ) => {
        const today = new Date();
        let from = new Date(today);
        let to = new Date(today);

        switch (type) {
            case 'yesterday':
                from.setDate(today.getDate() - 1);
                to = new Date(from);
                break;
            case 'thisWeek': {
                const day = today.getDay();
                const diffToMonday = day === 0 ? -6 : 1 - day;
                from.setDate(today.getDate() + diffToMonday);
                to = new Date(from);
                to.setDate(from.getDate() + 6);
                break;
            }
            case 'lastWeek': {
                const day = today.getDay();
                const diffToMonday = day === 0 ? -6 : 1 - day;
                from.setDate(today.getDate() + diffToMonday - 7);
                to = new Date(from);
                to.setDate(from.getDate() + 6);
                break;
            }
            case 'thisMonth':
                from = new Date(today.getFullYear(), today.getMonth(), 1);
                to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;
            case 'lastMonth':
                from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                to = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
        }

        setRange({ from, to });
        setOpen(false);
    };

    const displayText =
        value && value[0] && value[1]
            ? `${formatDate(value[0])} - ${formatDate(value[1])}`
            : 'mm/dd/yyyy - mm/dd/yyyy';

	const handleResetRange = () => {
		setRange(undefined);
		onChange(null)
	}

    return (
        <div className={`relative w-full ${containerStyle}`} ref={ref}>
            <label
                className={`block mb-1 dark:text-white text-[#414651] text-sm ${labelStyle}`}>
                {label}
            </label>
            <div
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between px-3 py-1.5 border dark:border-[#374151] rounded bg-white dark:text-white cursor-pointer min-w-72 dark:bg-[#333333] dark:border-transparent">
                <span
                    className={`${
                        value && value[0] && value[1]
                            ? ''
                            : 'text-primary-light'
                    }`}>
                    {displayText}
                </span>
                <div className="flex flex-row items-center gap-1">
                    <img
                        src={
                            theme === 'dark'
                                ? '/assets/insights-tab/calendar-white.svg'
                                : '/assets/insights-tab/calendar.svg'
                        }
                        className="object-contain w-[24px]"
                    />
                    {value && (
                        <button onClick={handleResetRange} type="button">
                            <img
                                alt="close"
                                src={
                                    theme === 'dark'
                                        ? '/svg/icons/close-white.svg'
                                        : '/svg/icons/close.svg'
                                }
                                className="object-contain w-3"
                            />
                        </button>
                    )}
                </div>
            </div>

            {open && (
                <div className="absolute right-0 mt-1 z-50 p-4 rounded-md shadow-lg bg-white dark:bg-black dark:text-white border dark:border-gray-700 flex gap-6">
                    <div className="flex flex-col text-sm w-40">
                        {[
                            { label: 'Today', key: 'today' },
                            { label: 'Yesterday', key: 'yesterday' },
                            { label: 'This Week', key: 'thisWeek' },
                            { label: 'Last Week', key: 'lastWeek' },
                            { label: 'This Month', key: 'thisMonth' },
                            { label: 'Last Month', key: 'lastMonth' },
                        ].map(({ label, key }) => (
                            <button
                                key={key}
                                onClick={() => applyShortcut(key as any)}
                                className={`text-left px-2 py-2 border-b border-b-1 dark:border-b-[#242424] rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                    key === 'thisWeekk'
                                        ? 'font-semibold text-blue-600 dark:text-blue-400'
                                        : ''
                                }`}>
                                {label}
                            </button>
                        ))}
                    </div>

                    <DayPicker
                        mode="range"
                        selected={range}
                        onSelect={setRange}
                        numberOfMonths={1}
                        defaultMonth={range?.from}
                        classNames={{
                            caption: 'dark:text-white',
                            head: 'dark:text-gray-300',
                            cell: 'dark:hover:bg-gray-800',
                        }}
                        modifiersClassNames={{
                            selected:
                                'bg-blue-500 text-white dark:bg-blue-400 dark:text-black',
                            range_start:
                                'bg-blue-500 text-white dark:bg-blue-400 dark:text-black',
                            range_end:
                                'bg-blue-500 text-white dark:bg-blue-400 dark:text-black',
                            range_middle:
                                'bg-blue-100 text-black dark:bg-blue-900 dark:text-white',
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default DateRangePopover;
